import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export default function Layout({ children }) {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div className="holographic-bg" />
      <div className="relative min-h-screen">
        {/* Navigation */}
        <nav className={`fixed top-0 left-[4.2mm] right-[4.2mm] z-50 transition-all duration-500 rounded-b-[2rem] ${
          isScrolled ? 'bg-[#00ffff]/50 backdrop-blur-md' : 'bg-transparent'
        }`}>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <div className="cyber-globe mr-3">
                    <div className="cyber-globe-sphere">
                      <div className="cyber-globe-meridians"></div>
                      <div className="cyber-globe-parallels"></div>
                    </div>
                  </div>
                  <span className="ml-2 text-xl font-bold text-white">Atlas AI</span>
                </Link>
              </div>
              
              {/* Mobile menu button */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-white hover:text-[#00ffff] transition-colors"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center">
                <div className="nav-oval-container mr-4">
                  <div className="flex items-center space-x-6">
                    <Link to="/services" className="text-white hover:text-[#00ffff] transition-colors">Services</Link>
                    <Link to="/pricing" className="text-white hover:text-[#00ffff] transition-colors">Pricing</Link>
                    <Link to="/testimonials" className="text-white hover:text-[#00ffff] transition-colors">Testimonials</Link>
                    <Link to="/about" className="text-white hover:text-[#00ffff] transition-colors">About Us</Link>
                  </div>
                </div>
                <div className="flex items-center">
                  <a
                    href={GOOGLE_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border-2 border-[#00ffff] text-[#00ffff] rounded-md hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors"
                  >
                    Get Started
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className={`px-2 pt-2 pb-3 space-y-1 transition-all duration-500 ${
                isScrolled ? 'bg-[#00ffff]/50 backdrop-blur-md' : 'bg-transparent'
              }`}>
                <Link
                  to="/services"
                  className="block px-3 py-2 text-white hover:text-[#00ffff] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link
                  to="/pricing"
                  className="block px-3 py-2 text-white hover:text-[#00ffff] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <Link
                  to="/testimonials"
                  className="block px-3 py-2 text-white hover:text-[#00ffff] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Testimonials
                </Link>
                <Link
                  to="/about"
                  className="block px-3 py-2 text-white hover:text-[#00ffff] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  About Us
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Main Content */}
        <main>
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-12">
            {/* Logo Section */}
            <div className="flex flex-col md:flex-row items-center justify-between mb-12">
              <div className="text-center md:text-left mb-8 md:mb-0">
                <span className="text-4xl sm:text-6xl md:text-8xl lg:text-[10rem] font-bold text-white tracking-wider">ATLAS</span>
              </div>
              <div className="cyber-globe" style={{ width: "120px", height: "120px", margin: "0 auto" }}>
                <div className="cyber-globe-sphere">
                  <div className="cyber-globe-meridians"></div>
                  <div className="cyber-globe-parallels"></div>
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 lg:gap-32 mb-12">
              {/* Company Links */}
              <div className="text-center md:text-left">
                <h3 className="font-semibold mb-4 text-white text-lg">Company</h3>
                <ul className="space-y-3">
                  <li><Link to="/about" className="text-white/60 hover:text-[#00ffff] transition-colors">About</Link></li>
                  <li><Link to="/careers" className="text-white/60 hover:text-[#00ffff] transition-colors">Careers</Link></li>
                  <li><Link to="/contact" className="text-white/60 hover:text-[#00ffff] transition-colors">Contact</Link></li>
                </ul>
              </div>

              {/* Legal Links */}
              <div className="text-center md:text-left">
                <h3 className="font-semibold mb-4 text-white text-lg">Legal</h3>
                <ul className="space-y-3">
                  <li><Link to="/privacy" className="text-white/60 hover:text-[#00ffff] transition-colors">Privacy</Link></li>
                  <li><Link to="/terms" className="text-white/60 hover:text-[#00ffff] transition-colors">Terms</Link></li>
                  <li><Link to="/security" className="text-white/60 hover:text-[#00ffff] transition-colors">Security</Link></li>
                </ul>
              </div>
            </div>

            <div className="text-center pt-8 border-t border-white/10">
              <p className="text-white/60">&copy; {new Date().getFullYear()} Atlas AI Business Solutions. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}